import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api";
import {
  normalizeLastLogins,
  normalizeMessages,
  normalizeSeen,
  normalizeSeenAt,
} from "./chatUtils";

const ALLOWED_USERS = ["A", "B"];
const PIN_LENGTH = 4;

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handlePasswordChange = (e) => {
    setPassword(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH));
  };

  const handleLogin = async () => {
    setError("");

    if (!ALLOWED_USERS.includes(username)) {
      setError("Tap User A or User B to continue.");
      return;
    }

    if (password.length !== PIN_LENGTH) {
      setError("Enter your 4-digit PIN.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/login", { username, password });
      if (res.data.success) {
        sessionStorage.clear();

        const normalizedUser = username.trim().toUpperCase();
        sessionStorage.setItem("username", normalizedUser);
        sessionStorage.setItem("token", res.data.token);
        sessionStorage.setItem(
          "messages",
          JSON.stringify(normalizeMessages(res.data.messages))
        );
        sessionStorage.setItem(
          "seen",
          JSON.stringify(normalizeSeen(res.data.seen))
        );
        sessionStorage.setItem(
          "seenAt",
          JSON.stringify(normalizeSeenAt(res.data.seenAt))
        );
        sessionStorage.setItem(
          "lastLogins",
          JSON.stringify(normalizeLastLogins(res.data.lastLogins))
        );

        navigate("/chat");
      }
    } catch (err) {
      const message =
        err.response?.data?.message || "Invalid username or PIN.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    ALLOWED_USERS.includes(username) && password.length === PIN_LENGTH;

  return (
    <div className="app-shell relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.35),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(14,165,233,0.18),_transparent_50%)]" />
      <div className="pointer-events-none absolute -left-20 top-10 h-56 w-56 rounded-full bg-indigo-500/20 blur-3xl sm:h-72 sm:w-72" />
      <div className="pointer-events-none absolute -right-10 bottom-0 h-56 w-56 rounded-full bg-cyan-500/20 blur-3xl sm:h-72 sm:w-72" />

      <div className="relative flex flex-1 flex-col px-4 pb-4 pt-6 sm:items-center sm:justify-center sm:px-6 sm:py-10">
        <div className="mx-auto w-full max-w-md flex-1 sm:flex-none">
          <div className="mb-6 text-center sm:mb-8">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-indigo-500/20 text-3xl shadow-lg shadow-indigo-500/10">
              💬
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Welcome back
            </h1>
            <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-400">
              Choose your account and enter your 4-digit PIN to start chatting.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-800 bg-slate-900/85 p-5 shadow-card backdrop-blur-xl sm:p-8">
            <div className="space-y-5">
              <div>
                <label className="mb-3 block text-sm font-medium text-slate-300">
                  Who are you?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {ALLOWED_USERS.map((user) => (
                    <button
                      key={user}
                      type="button"
                      onClick={() => setUsername(user)}
                      className={`user-pill ${
                        username === user
                          ? "user-pill-active"
                          : "user-pill-inactive"
                      }`}
                    >
                      <span className="text-lg font-bold">{user}</span>
                      <span className="text-xs text-slate-400">User {user}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-3 block text-sm font-medium text-slate-300"
                >
                  4-digit PIN
                </label>
                <input
                  id="password"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={PIN_LENGTH}
                  autoComplete="one-time-code"
                  enterKeyHint="go"
                  placeholder="••••"
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={(e) => e.key === "Enter" && canSubmit && handleLogin()}
                  className="input-field text-center text-2xl tracking-[0.5em]"
                />
                <p className="mt-2 text-center text-xs text-slate-500">
                  {password.length}/{PIN_LENGTH} digits
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
                >
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 mx-auto w-full max-w-md pt-4 sm:static sm:pt-6">
          <button
            type="button"
            onClick={handleLogin}
            disabled={loading || !canSubmit}
            className="btn-primary w-full"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
